import type { ReactNode } from 'react';

// 通用状态标签
export function StatusBadge({
  status,
  maps,
}: {
  status: string;
  maps: Record<string, { label: string; className: string; icon: string }>;
}) {
  const cfg = maps[status] || maps.default;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.className}`}
    >
      <span className="w-3 h-3 flex items-center justify-center">
        <i className={cfg.icon}></i>
      </span>
      {cfg.label}
    </span>
  );
}

// 统计卡片
export function StatCard({
  icon,
  label,
  value,
  tone = 'gold',
}: {
  icon: string;
  label: string;
  value: ReactNode;
  tone?: 'gold' | 'brown' | 'green' | 'red';
}) {
  const toneMap = {
    gold: 'bg-gold-100 text-gold-700',
    brown: 'bg-brown-100 text-brown-700',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-500',
  };
  return (
    <div className="bg-white rounded-xl border border-brown-100 p-4 md:p-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${toneMap[tone]}`}>
        <span className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-xl`}></i>
        </span>
      </div>
      <p className="text-brown-400 text-xs mb-1">{label}</p>
      <p className="text-brown-900 text-xl md:text-2xl font-bold leading-none">{value}</p>
    </div>
  );
}

// 白色区块卡片
export function SectionCard({
  title,
  action,
  children,
  className = '',
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white rounded-xl border border-brown-100 ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between px-4 md:px-5 py-3.5 border-b border-brown-100">
          {title && <h3 className="text-brown-900 text-sm font-bold">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// 空状态
export function EmptyState({ title, desc }: { title: string; desc?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-brown-50 flex items-center justify-center mb-3">
        <span className="w-7 h-7 flex items-center justify-center text-brown-300">
          <i className="ri-inbox-line text-2xl"></i>
        </span>
      </div>
      <p className="text-brown-500 text-sm">{title}</p>
      {desc && <p className="text-brown-300 text-xs mt-1">{desc}</p>}
    </div>
  );
}

// 加载状态
export function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
      <p className="text-brown-400 text-xs">加载中...</p>
    </div>
  );
}

// 错误状态
export function ErrorState({ onRetry, message }: { onRetry: () => void; message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <div className="w-12 h-12 rounded-full bg-brown-50 flex items-center justify-center">
        <span className="w-6 h-6 flex items-center justify-center text-brown-400">
          <i className="ri-error-warning-line text-xl"></i>
        </span>
      </div>
      <p className="text-brown-500 text-sm">{message || '数据加载失败'}</p>
      <button
        onClick={onRetry}
        className="px-5 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors"
      >
        重新加载
      </button>
    </div>
  );
}

// 确认弹窗
export function ConfirmDialog({
  open,
  title,
  desc,
  confirmText = '确认',
  danger = false,
  onCancel,
  onConfirm,
  loading = false,
  children,
}: {
  open: boolean;
  title: string;
  desc?: string;
  confirmText?: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
  children?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel}></div>
      <div className="relative bg-white rounded-xl w-full max-w-sm p-5 animate-fade-up">
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              danger ? 'bg-red-50' : 'bg-gold-100'
            }`}
          >
            <span
              className={`w-5 h-5 flex items-center justify-center ${
                danger ? 'text-red-500' : 'text-gold-600'
              }`}
            >
              <i className={danger ? 'ri-alert-line text-xl' : 'ri-question-line text-xl'}></i>
            </span>
          </div>
          <div>
            <h3 className="text-brown-900 text-base font-bold">{title}</h3>
            {desc && <p className="text-brown-500 text-xs mt-1">{desc}</p>}
          </div>
        </div>
        {children && <div className="mb-4">{children}</div>}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-lg border border-brown-200 text-brown-600 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-lg text-white text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 ${
              danger ? 'bg-red-500 hover:bg-red-600' : 'bg-gold-500 hover:bg-gold-600'
            }`}
          >
            {loading ? '处理中...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}